/**
 * strengthEvents — Build and insert strength_events from a workout payload.
 *
 * Extracted from record.tsx to keep the save flow modular.
 */
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { supabase } from '@/lib/supabase';
import { normalizeLiftKey, normalizeToV1Lift } from '@/lib/lifts/canonical';
import { createLogger } from '@/lib/logger';

const CANONICAL_NAME_MAP: Record<string, string> = {
  'bench': 'Bench Press',
  'squat': 'Back Squat',
  'deadlift': 'Deadlift',
  'pull-up': 'Weighted Pull-Up',
  'weighted-pull-up': 'Weighted Pull-Up',
  'weighted_pull_up': 'Weighted Pull-Up',
  'dead-hang': 'Dead Hang',
  'dead_hang': 'Dead Hang',
  'farmers-carry': "Farmer's Carry Static Hold",
  'farmers_carry': "Farmer's Carry Static Hold",
};

const log = createLogger('StrengthEvents');

export interface StrengthEventProfile {
  id: string;
  units?: string;
  bodyweight?: number;
}

export interface WorkoutPayload {
  exercises: {
    exercise_id: string;
    name: string;
    sets: { reps: number; weight: number; is_pr: boolean; media_url: string | null }[];
    notes: string | null;
    rpe: number | null;
    equipment: string | null;
    supersetGroupId: string | null;
    is_competition: boolean;
  }[];
  units: string;
  workoutName: string;
  sessionNotes: string | null;
  supersetGroups: Record<string, string[]> | null;
}

function liftKeyForName(name: string): string | null {
  if (!name) return null;
  // Prefer V1 lift keys (back-squat, bench-press, etc.) over legacy keys (squat, bench)
  const v1Key = normalizeToV1Lift(name);
  if (v1Key) return v1Key;
  // Fall back to legacy normalizer for non-V1 exercises
  const { canonical, normalizedRaw } = normalizeLiftKey(name);
  if (__DEV__) {
    const rawLower = String(name || '').toLowerCase();
    if (!canonical && /(bench|squat|dead)/.test(rawLower)) {
      log.warn('[LiftNormalize] unknown variant', { raw: name, normalizedRaw });
    }
  }
  return canonical || normalizedRaw;
}

export interface StrengthEventRow {
  profile_id: string;
  lift_key: string;
  canonical_name: string;
  weight: number;
  weight_unit: string;
  reps: number;
  performed_at: string;
  metric_key: string;
  formula_version: number;
  quality_tier: number;
  range_of_motion: string;
  client_platform: string;
  client_app_version: string;
  timezone_offset_minutes: number;
  source: string;
}

export function buildStrengthEventRows(
  profile: StrengthEventProfile,
  workoutPayload: WorkoutPayload,
  performedAt: string,
): StrengthEventRow[] {
  const rawUnits = String(profile.units || '').toLowerCase();
  const weightUnitDefault = rawUnits === 'kg' ? 'kg' : 'lb';
  const profileBodyweight = Number(profile.bodyweight) || 0;
  const clientPlatform = Platform.OS === 'web' ? 'web' : Platform.OS;
  const clientAppVersion =
    (Constants.expoConfig as any)?.version ||
    (Constants as any)?.manifest?.version ||
    'dev';
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();

  const rows: StrengthEventRow[] = [];
  for (const ex of workoutPayload.exercises) {
    const lk = liftKeyForName(ex.name || '');
    for (const s of ex.sets || []) {
      let w = Number(s.weight || 0);
      const r = Number(s.reps || 0);
      // For pull-up variants: store external load as-is (leaderboard adds BW when scoring).
      // Exception: bodyweight-only pull-ups (w <= 0) store profileBodyweight so the row
      // has a non-zero weight and leaderboard can still compute a score.
      const isPullUpVariant = lk === 'weighted-pull-up' || lk === 'pull-up' || lk === 'pull-ups' || lk === 'pullup' || lk === 'pullups' || lk === 'chin-up' || lk === 'chin-ups';
      if (isPullUpVariant) {
        if (w <= 0 && profileBodyweight > 0) {
          // Bodyweight only: store bodyweight so the row isn't skipped
          w = profileBodyweight;
        }
        // w > 0: keep external load as-is; leaderboard will add BW on top
      }
      // Dead hang is bodyweight-only timed exercise — reps = seconds, weight not required
      const isDeadHang = lk === 'dead-hang';
      if (isDeadHang && w <= 0) {
        if (profileBodyweight > 0) {
          w = profileBodyweight;
        } else {
          log.warn('Dead hang skipped: bodyweight is 0, cannot compute score', { profileId: profile.id });
          continue; // Skip this event — dead hang requires bodyweight
        }
      }
      if ((!w || w <= 0) && !isPullUpVariant && !isDeadHang) continue;
      if (!Number.isInteger(r) || r < 1) continue;

      const finalLiftKey = lk || (ex.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      rows.push({
        profile_id: profile.id,
        lift_key: finalLiftKey,
        canonical_name: CANONICAL_NAME_MAP[finalLiftKey] || ex.name || finalLiftKey,
        weight: w,
        weight_unit: weightUnitDefault,
        reps: r,
        performed_at: performedAt,
        metric_key: 'e1rm',
        formula_version: 1,
        quality_tier: 1,
        range_of_motion: 'full',
        client_platform: clientPlatform,
        client_app_version: clientAppVersion,
        timezone_offset_minutes: timezoneOffsetMinutes,
        source: 'workout',
      });
    }
  }
  return rows;
}

/**
 * Insert strength events via edge function, with DEV fallback to direct insert.
 */
export async function insertStrengthEvents(
  profile: StrengthEventProfile,
  workoutPayload: WorkoutPayload,
  performedAt: string,
  workoutId?: string,
): Promise<{ inserted: number; skipped: number }> {
  const rows = buildStrengthEventRows(profile, workoutPayload, performedAt);
  const clientAppVersion =
    (Constants.expoConfig as any)?.version ||
    (Constants as any)?.manifest?.version ||
    'dev';

  if (__DEV__) {
    const liftNames = workoutPayload.exercises.map((ex) => ex.name);
    log.info('REQUEST SUMMARY', {
      profileId: profile.id,
      workoutId,
      performed_at: performedAt,
      exerciseCount: workoutPayload.exercises.length,
      liftNames,
      rowsBuilt: rows.length,
      sampleRow: rows[0] ?? null,
    });
  }

  if (rows.length === 0) {
    log.warn('SKIPPED — 0 valid rows built', {
      exerciseCount: workoutPayload.exercises.length,
      profileId: profile.id,
    });
    return { inserted: 0, skipped: 0 };
  }

  const fnRequestBody = {
    performed_at: performedAt,
    payload: workoutPayload,
    profile_bodyweight: profile.bodyweight || null,
    client_context: {
      client_platform: Platform.OS,
      client_app_version: clientAppVersion,
      timezone_offset_minutes: -(new Date().getTimezoneOffset()),
    },
  };

  log.info('invoking edge function with', rows.length, 'rows');

  const { data: fnData, error: fnError } = await supabase.functions.invoke(
    'insert_strength_events',
    { body: fnRequestBody },
  );

  if (__DEV__) {
    log.info('RESPONSE', {
      fnError: fnError
        ? { message: fnError.message, name: fnError.name, status: (fnError as any)?.status }
        : null,
      fnData,
      workoutId,
      profileId: profile.id,
    });
  }

  if (fnError || !fnData?.ok) {
    log.warn('edge function FAILED', {
      message: fnError?.message || fnData?.error?.message || 'unknown',
      fnData,
      workoutId,
      profileId: profile.id,
    });

    // DEV fallback: direct insert
    if (__DEV__ && rows.length > 0) {
      log.info('DEV FALLBACK: attempting direct insert of', rows.length, 'rows');
      try {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('strength_events')
          .insert(rows)
          .select('id,lift_key');

        if (fallbackError) {
          log.error('DEV FALLBACK insert FAILED', {
            message: fallbackError.message,
            code: fallbackError.code,
            details: fallbackError.details,
            hint: fallbackError.hint,
          });
          if (Platform.OS === 'web') {
            alert(
              `[DEV] strength_events direct insert failed: ${fallbackError.message}\nCode: ${fallbackError.code}\nHint: ${fallbackError.hint || 'none'}`,
            );
          }
        } else {
          log.info('DEV FALLBACK insert OK', {
            inserted: fallbackData?.length ?? 0,
            liftKeys: fallbackData?.map((r: any) => r.lift_key),
          });
          if (Platform.OS === 'web') {
            alert(`[DEV] strength_events fallback inserted ${fallbackData?.length ?? 0} rows`);
          }
          return { inserted: fallbackData?.length ?? 0, skipped: 0 };
        }
      } catch (fallbackEx: any) {
        log.error('DEV FALLBACK exception', fallbackEx.message);
      }
    }
    return { inserted: 0, skipped: 0 };
  }

  log.info('edge function OK', {
    inserted: fnData.inserted,
    skipped: fnData.skipped,
  });
  return { inserted: fnData.inserted ?? 0, skipped: fnData.skipped ?? 0 };
}
